import { z } from 'zod'
import type { ConversationListItem } from '../api'

/**
 * CRM shared types: workstream D (record screens, conversations, merges, duplicates, roles and
 * relationships, lineage, import history). Routes in apps/api/src/http/console/crmPeople.ts.
 */

// ---------------------------------------------------------------------------
// Roles and relationships (design 7.2 "roles, relationships")
// ---------------------------------------------------------------------------
/** Structured roles a person holds at an organisation, which policy and the AI can rely on. */
export const PERSON_ROLES = ['books', 'approves_purchases', 'billing', 'decision_maker', 'technical', 'other'] as const
export const PersonRole = z.enum(PERSON_ROLES)
export type PersonRole = z.infer<typeof PersonRole>
export const PERSON_ROLE_LABELS: Record<PersonRole, string> = {
  books: 'Books', approves_purchases: 'Approves purchases', billing: 'Billing contact', decision_maker: 'Decision maker', technical: 'Technical contact', other: 'Other',
}

export const RELATIONSHIP_KINDS = ['parent', 'child', 'guardian', 'partner', 'assistant', 'manager', 'colleague', 'referred_by', 'other'] as const
export const RelationshipKind = z.enum(RELATIONSHIP_KINDS)
export type RelationshipKind = z.infer<typeof RelationshipKind>
/** How a relationship reads from the other person's side. */
export const RELATIONSHIP_INVERSE: Record<RelationshipKind, RelationshipKind> = {
  parent: 'child', child: 'parent', guardian: 'other', partner: 'partner', assistant: 'manager', manager: 'assistant', colleague: 'colleague', referred_by: 'other', other: 'other',
}

export interface MembershipView {
  id: string | null                      // null for the main organisation when no roles were recorded
  organisation: { id: string; name: string }
  roles: PersonRole[]
  title: string | null
  main: boolean                          // people.organisation_id
  primary: boolean                       // the organisation's primary contact
}
export interface RelationshipView { id: string; person: { id: string; name: string }; kind: RelationshipKind; direction: 'outgoing' | 'incoming'; note: string | null }

export const MembershipInput = z.object({
  organisation_id: z.uuid(),
  roles: z.array(PersonRole).max(PERSON_ROLES.length).default([]),
  title: z.string().trim().max(200).nullish(),
  /** Make this their main organisation (people.organisation_id). */
  main: z.boolean().optional(),
})
export const RelationshipInput = z.object({ related_person_id: z.uuid(), kind: RelationshipKind, note: z.string().trim().max(500).nullish() })
export const OwnerInput = z.object({ owner_id: z.uuid().nullable() })
export const PrimaryInput = z.object({ person_id: z.uuid().nullable() })

/** Staff who can own a record (GET /crm/owners). */
export interface OwnerOption { id: string; name: string; role: string }

/** Who is merged into whom (reversible). */
export interface MergeLink { id: string; name: string; change_id: string | null; at: string | null }

/** GET /crm/people/:id/links: what the person record shows beyond PersonView. */
export interface PersonLinks {
  owner: { id: string; name: string } | null
  memberships: MembershipView[]
  relationships: RelationshipView[]
  lineage: Lineage | null
  merged_into: MergeLink | null
  merged_from: MergeLink[]
}
/** GET /crm/organisations/:id/links. */
export interface OrganisationLinks {
  owner: { id: string; name: string } | null
  primary_person_id: string | null
  members: { person: { id: string; name: string }; roles: PersonRole[]; title: string | null; main: boolean; primary: boolean; membership_id: string | null }[]
  lineage: Lineage | null
  merged_into: MergeLink | null
  merged_from: MergeLink[]
}

// ---------------------------------------------------------------------------
// Lineage (design 7.5 "each record shows where it came from")
// ---------------------------------------------------------------------------
export interface Lineage {
  /** How the record began. */
  origin: 'import' | 'staff' | 'api' | 'ai' | 'agent' | 'channel' | 'system' | 'customer' | 'unknown'
  /** In plain words, e.g. "Imported from bookings.csv, row 14" or "Added by Kim Lee". */
  label: string
  at: string
  import: { id: string; filename: string | null; row: number | null } | null
  channel: string | null
  /** Imports that later updated this record. */
  updated_by_imports: { id: string; filename: string | null; at: string }[]
}

// ---------------------------------------------------------------------------
// Merges and duplicates (design 7.5 "the AI finds duplicates"; brief: merges are reversible)
// ---------------------------------------------------------------------------
export interface MergePreview {
  from: { id: string; name: string }
  into: { id: string; name: string }
  /** What would move, table by table, with counts. */
  moves: { what: string; count: number }[]
  warnings: string[]
}
export const MergeInput = z.object({ into: z.uuid() })

export type DuplicateKind = 'person' | 'organisation'
export interface DuplicateParty { id: string; name: string; detail: string | null }
export interface DuplicatePair {
  id: string | null                      // the stored pair, once decided or judged
  kind: DuplicateKind
  a: DuplicateParty
  b: DuplicateParty
  score: number                          // 0..1, from the fuzzy match
  reasons: string[]                      // "Same phone number", "Names 86% alike" ...
  ai: { same: boolean; confidence: number; reason: string } | 'unavailable' | null
  status: 'open' | 'dismissed' | 'merged'
}
export const DuplicateCheckInput = z.object({
  kind: z.enum(['person', 'organisation']),
  name: z.string().trim().max(300).default(''),
  emails: z.array(z.string()).max(10).default([]),
  phones: z.array(z.string()).max(10).default([]),
  domain: z.string().trim().max(300).nullish(),
})
export interface DuplicateMatch { id: string; name: string; detail: string | null; score: number; reasons: string[] }

// ---------------------------------------------------------------------------
// Imports history (design 7.5 "imports can be rolled back")
// ---------------------------------------------------------------------------
export interface ImportListItem {
  id: string; target: string; filename: string | null; status: string; rows_total: number
  created_by: string | null; created_at: string; applied_at: string | null
  applied: { created: number; updated: number; skipped: number } | null
}
export interface RollbackCheck {
  /** Records the import touched that someone else has changed since; rolling back overwrites those changes. */
  edited_since: { entity: string; id: string; name: string; changed_at: string; by: string }[]
  created: number
  updated: number
}
export interface ImportRowView {
  row: number
  action: 'create' | 'update' | 'skip' | 'error'
  message: string | null
  match: { id: string; name: string } | null
  /** For updates: each field that would change, from what to what. For creates: the new values. */
  changes: { field: string; before: unknown; after: unknown }[]
}

// ---------------------------------------------------------------------------
// Conversations in the CRM (screen 5 rail) and staff contact from a record
// ---------------------------------------------------------------------------
export type CrmConversationItem = ConversationListItem
export const ContactInput = z.object({
  channel: z.enum(['email', 'sms']),
  subject: z.string().trim().max(300).nullish(),
  text: z.string().trim().min(1, 'Write the message').max(20_000),
})
export const CallLogInput = z.object({
  outcome: z.enum(['answered', 'no_answer', 'voicemail', 'wrong_number']),
  notes: z.string().trim().max(5000).default(''),
  minutes: z.number().int().min(0).max(600).default(0),
  direction: z.enum(['outbound', 'inbound']).default('outbound'),
})

/** Display names for references (record fields of type reference). */
export const RefsInput = z.object({ refs: z.array(z.object({ object: z.string().min(1), id: z.uuid() })).max(200) })
export interface RefName { object: string; id: string; name: string | null }
